import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '@/store';
import { supabase } from '@/services/supabase';
import { clearUserContext, setUserContext } from '@/services/sentry';
import type { User } from '@/types';
import type { Session, AuthChangeEvent } from '@supabase/supabase-js';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, name?: string) => Promise<{ error: string | null }>;
  signInWithMagicLink: (email: string) => Promise<{ error: string | null }>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function mapSessionUser(sessionUser: Session['user']): User {
  return {
    id: sessionUser.id,
    email: sessionUser.email ?? '',
    name: sessionUser.user_metadata?.['name'] as string | undefined,
    avatar: sessionUser.user_metadata?.['avatar_url'] as string | undefined,
    role: 'user',
    created_at: sessionUser.created_at,
    updated_at: sessionUser.updated_at ?? sessionUser.created_at,
  };
}

async function fetchProfile(userId: string): Promise<Partial<User>> {
  const { data } = await supabase
    .from('profiles')
    .select('name, avatar_url, role, organization_id')
    .eq('id', userId)
    .maybeSingle();

  if (!data) return {};
  return {
    name: data.name ?? undefined,
    avatar: data.avatar_url ?? undefined,
    role: data.role as User['role'],
    organization_id: data.organization_id ?? undefined,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { setUser, setLoading, clear } = useUserStore();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      if (!mounted) return;
      setSession(initialSession);
      if (initialSession?.user) {
        const baseUser = mapSessionUser(initialSession.user);
        setUser(baseUser);
        setUserContext({ id: baseUser.id, email: baseUser.email });
        (async () => {
          const profile = await fetchProfile(initialSession.user.id);
          if (mounted) {
            setUser({ ...baseUser, ...profile });
          }
        })();
      } else {
        setUser(null);
        clearUserContext();
      }
      setIsLoading(false);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, newSession: Session | null) => {
      if (!mounted) return;
      setSession(newSession);

      if (event === 'SIGNED_OUT') {
        clear();
        clearUserContext();
        queryClient.clear();
        return;
      }

      if (newSession?.user) {
        const baseUser = mapSessionUser(newSession.user);
        setUser(baseUser);
        setUserContext({ id: baseUser.id, email: baseUser.email });
        (async () => {
          const profile = await fetchProfile(newSession.user.id);
          if (mounted) {
            setUser({ ...baseUser, ...profile });
          }
        })();
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [setUser, setLoading, clear, queryClient]);

  const user = useUserStore((s) => s.user);

  const contextValue = useMemo<AuthContextValue>(() => ({
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    signIn: async (email, password) => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: error.message };
      return { error: null };
    },
    signUp: async (email, password, name) => {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { name } },
      });
      if (error) return { error: error.message };
      return { error: null };
    },
    signInWithMagicLink: async (email) => {
      const { error } = await supabase.auth.signInWithOtp({ email });
      if (error) return { error: error.message };
      return { error: null };
    },
    signInWithGoogle: async () => {
      const { error } = await supabase.auth.signInWithOAuth({ provider: 'google' });
      if (error) return { error: error.message };
      return { error: null };
    },
    signOut: async () => {
      await supabase.auth.signOut();
    },
  }), [user, session, isLoading]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
